import React from 'react';

const DENSITY_RATIO = { none: 0, light: 0.33, moderate: 0.67, dense: 1 };
const DENSITY_LABEL = { light: 'Light', moderate: 'Moderate', dense: 'Dense' };
const GOAL_LABEL = { narrow: 'Single plan', focused: 'Two-plan', broad: 'Many paths' };
const PATH_LABEL = { combo: 'Combo', combat: 'Combat', alternate: 'Alternate', attrition: 'Attrition' };
const CURVE_SHAPE_LABEL = {
    'bottom-heavy': 'Low curve',
    balanced: 'Balanced curve',
    'top-heavy': 'High curve',
};

function capitalize(s) {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function DensityPill({ density }) {
    if (!density || density === 'none') return null;
    return (
        <span className={`powerFacetDensity powerFacetDensity--${density}`}>{DENSITY_LABEL[density]}</span>
    );
}

function FacetHeader({ title, density, headline }) {
    return (
        <div className="powerFacetHeader">
            <div className="powerFacetTitleRow">
                <span className="powerFacetTitle">{title}</span>
                <DensityPill density={density} />
            </div>
            {headline && <span className="powerFacetHeadline">{headline}</span>}
        </div>
    );
}

function StatRow({ stats }) {
    const visible = (stats || []).filter((s) => s && s.value != null && s.value !== '');
    if (visible.length === 0) return null;
    return (
        <div className="powerFacetStats">
            {visible.map((s, i) => (
                <span key={i} className="powerFacetStat">
                    <strong>{s.value}</strong> {s.label}
                </span>
            ))}
        </div>
    );
}

function DensityRow({ label, density }) {
    const ratio = DENSITY_RATIO[density] ?? 0;
    const pct = Math.round(ratio * 100);
    const isNone = !density || density === 'none';
    return (
        <div className={`powerFacetDensityRow${isNone ? ' powerFacetDensityRow--none' : ''}`}>
            <span className="powerFacetDensityRowLabel">{label}</span>
            <div className="powerFacetDensityRowBar">
                <div
                    className={`powerFacetDensityRowFill powerFacetDensityRowFill--${density || 'none'}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <span className="powerFacetDensityRowValue">{isNone ? '—' : DENSITY_LABEL[density]}</span>
        </div>
    );
}

function Badges({ items }) {
    const visible = (items || []).filter(Boolean);
    if (visible.length === 0) return null;
    return (
        <div className="powerFacetBadges">
            {visible.map((b, i) => (
                <span key={i} className={`powerFacetBadge powerFacetBadge--${b.tone || 'warn'}`}>
                    {b.label}
                </span>
            ))}
        </div>
    );
}

function BigNumber({ value, label }) {
    if (value == null) return null;
    return (
        <div className="powerFacetBigNumber">
            <span className="powerFacetBigValue">{value}</span>
            <span className="powerFacetBigLabel">{label}</span>
        </div>
    );
}

function RampFacet({ ramp }) {
    if (!ramp || ramp.total === 'none') return null;
    const stats = [
        { value: ramp.rockCount || null, label: 'rocks' },
        { value: ramp.dorkCount || null, label: 'dorks' },
        { value: ramp.ritualCount || null, label: 'rituals' },
        { value: ramp.treasureCount || null, label: 'treasure' },
        { value: ramp.landRampCount || null, label: 'land ramp' },
    ];
    const badges = [
        ramp.fastMana === 'dense' && { tone: 'alert', label: 'Fast mana package' },
        ramp.fastMana === 'moderate' && { tone: 'warn', label: 'Some fast mana' },
        ramp.fastMana === 'light' && { tone: 'warn', label: 'Light fast mana' },
        ramp.curveAcceleration === 'dense' && { tone: 'warn', label: 'Curve-accelerating lands' },
        ramp.curveAcceleration === 'moderate' && { tone: 'warn', label: 'Some curve acceleration' },
        ramp.curveAcceleration === 'light' && { tone: 'warn', label: 'Light curve acceleration' },
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Ramp" density={ramp.total} />
            <StatRow stats={stats} />
            <Badges items={badges} />
        </div>
    );
}

function TutorsFacet({ tutors }) {
    if (!tutors || tutors.density === 'none') return null;
    const quality = tutors.avgQuality;
    const stats = quality != null ? [{ value: quality.toFixed(1), label: 'avg quality' }] : [];
    const badges = [
        quality > 10 && { tone: 'alert', label: 'High-fidelity' },
        quality > 5 && quality <= 10 && { tone: 'warn', label: 'Conditional' },
        tutors.manabaseOverlap && { tone: 'ok', label: 'Reinforces manabase' },
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Tutors" density={tutors.density} />
            <StatRow stats={stats} />
            <Badges items={badges} />
        </div>
    );
}

function InteractionFacet({ interaction }) {
    if (!interaction) return null;
    const rows = [
        { label: 'Counterspells', density: interaction.counterspells },
        { label: 'Removal', density: interaction.removal },
        { label: 'Board wipes', density: interaction.boardWipes },
        { label: 'Protection', density: interaction.protection },
        { label: 'Graveyard hate', density: interaction.graveyardHate },
    ];
    if (!rows.some((r) => r.density && r.density !== 'none') && interaction.overall === 'none') return null;
    return (
        <div className="powerFacet powerFacet--wide">
            <FacetHeader title="Interaction" density={interaction.overall} />
            <div className="powerFacetDensityRows">
                {rows.map((r) => (
                    <DensityRow key={r.label} label={r.label} density={r.density} />
                ))}
            </div>
        </div>
    );
}

function ResourceDenialFacet({ resourceDenial }) {
    if (!resourceDenial) return null;
    const rows = [
        { label: 'Stax', density: resourceDenial.stax },
        { label: 'Taxes', density: resourceDenial.taxes },
        { label: 'Discard', density: resourceDenial.discard },
    ];
    if (!rows.some((r) => r.density && r.density !== 'none')) return null;
    return (
        <div className="powerFacet">
            <FacetHeader title="Resource denial" />
            <div className="powerFacetDensityRows">
                {rows.map((r) => (
                    <DensityRow key={r.label} label={r.label} density={r.density} />
                ))}
            </div>
        </div>
    );
}

function WinconsFacet({ wincons }) {
    if (!wincons || !Array.isArray(wincons.paths) || wincons.paths.length === 0) return null;
    return (
        <div className="powerFacet">
            <FacetHeader title="Win conditions" headline={GOAL_LABEL[wincons.goal]} />
            <div className="powerFacetBadges">
                {wincons.paths.map((p) => (
                    <span
                        key={p}
                        className={`powerFacetBadge powerFacetBadge--${p === wincons.primary ? 'alert' : 'warn'}`}
                    >
                        {PATH_LABEL[p] || p}
                    </span>
                ))}
            </div>
            <StatRow
                stats={[
                    { value: wincons.count, label: wincons.count === 1 ? 'path' : 'paths' },
                    wincons.mixedTypes ? { value: '✓', label: 'mixed types' } : null,
                ]}
            />
        </div>
    );
}

function CombosFacet({ combos }) {
    if (!combos || (!combos.count && !combos.effectiveLines)) return null;
    const stats = [
        { value: combos.effectiveLines, label: combos.effectiveLines === 1 ? 'line' : 'lines' },
        combos.winconCount ? { value: combos.winconCount, label: 'wincons' } : null,
        combos.count && combos.count !== combos.effectiveLines
            ? { value: combos.count, label: 'total' }
            : null,
        typeof combos.redundancy === 'number' && combos.redundancy > 0
            ? { value: `${combos.redundancy.toFixed(2)}×`, label: 'redundancy' }
            : null,
    ];
    const badges = [
        combos.hasTwoCardLine && { tone: 'alert', label: 'Two-card line' },
        combos.isolatedFromWincon && { tone: 'warn', label: 'Value only' },
        combos.enablerDense && { tone: 'warn', label: 'Enabler-dense, no finisher' },
        (combos.redundancy || 0) > 1.5 && { tone: 'alert', label: 'Redundant' },
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Combos" />
            <StatRow stats={stats} />
            <Badges items={badges} />
        </div>
    );
}

function CardAdvantageFacet({ cardAdvantage }) {
    if (!cardAdvantage || (cardAdvantage.draw === 'none' && cardAdvantage.recursion === 'none')) return null;
    return (
        <div className="powerFacet">
            <FacetHeader title="Card advantage" />
            <div className="powerFacetDensityRows">
                <DensityRow label="Draw" density={cardAdvantage.draw} />
                <DensityRow label="Recursion" density={cardAdvantage.recursion} />
            </div>
        </div>
    );
}

function SynergyFacet({ synergy }) {
    if (!synergy || synergy.density === 'none') return null;
    const badges = [
        synergy.concentrated && { tone: 'alert', label: 'Concentrated (narrow + dense)' },
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Synergy" density={synergy.density} />
            <Badges items={badges} />
        </div>
    );
}

function ManabaseFacet({ manabase }) {
    if (!manabase || !manabase.landCount) return null;
    const colorWord =
        manabase.colorCount === 0
            ? 'Colorless'
            : manabase.colorCount === 5
            ? 'WUBRG'
            : `${manabase.colorCount}-color`;
    const basic = manabase.basicCount || 0;
    const nonbasic = manabase.nonbasicCount || 0;
    const partitionTotal = basic + nonbasic;
    const basicPct = partitionTotal > 0 ? (basic / partitionTotal) * 100 : 0;
    const nonbasicPct = partitionTotal > 0 ? (nonbasic / partitionTotal) * 100 : 0;
    const tags = [];
    if (manabase.fetchCount) tags.push(`${manabase.fetchCount} fetch`);
    if (manabase.utilityLandCount) tags.push(`${manabase.utilityLandCount} utility`);
    return (
        <div className="powerFacet">
            <FacetHeader title="Manabase" density={manabase.optimization} headline={colorWord} />
            <BigNumber value={manabase.landCount} label="lands" />
            {partitionTotal > 0 && (
                <div className="powerFacetStackedBar" title={`${basic} basic · ${nonbasic} nonbasic`}>
                    {basic > 0 && (
                        <div
                            className="powerFacetStackedSeg powerFacetStackedSeg--basic"
                            style={{ width: `${basicPct}%` }}
                        >
                            <span>{basic} basic</span>
                        </div>
                    )}
                    {nonbasic > 0 && (
                        <div
                            className="powerFacetStackedSeg powerFacetStackedSeg--nonbasic"
                            style={{ width: `${nonbasicPct}%` }}
                        >
                            <span>{nonbasic} nonbasic</span>
                        </div>
                    )}
                </div>
            )}
            {tags.length > 0 && (
                <span className="powerFacetSubNote">includes {tags.join(' · ')}</span>
            )}
        </div>
    );
}

function CompositionFacet({ composition }) {
    if (!composition || !composition.nonlandCount) return null;
    const pct = Math.round((composition.permanentRatio || 0) * 100);
    const types = [
        { key: 'creature', label: 'Creatures', value: composition.creatureCount },
        { key: 'artifact', label: 'Artifacts', value: composition.artifactCount },
        { key: 'enchantment', label: 'Enchant.', value: composition.enchantmentCount },
        { key: 'instant', label: 'Instants', value: composition.instantCount },
        { key: 'sorcery', label: 'Sorceries', value: composition.sorceryCount },
        { key: 'planeswalker', label: 'PW', value: composition.planeswalkerCount },
        { key: 'battle', label: 'Battles', value: composition.battleCount },
    ].filter((t) => t.value > 0);
    const max = types.reduce((m, t) => Math.max(m, t.value), 0);
    return (
        <div className="powerFacet powerFacet--wide">
            <FacetHeader
                title="Composition"
                headline={`${composition.nonlandCount} nonland · ${pct}% permanents`}
            />
            <div className="powerFacetTypeBars">
                {types.map((t) => {
                    const w = max > 0 ? (t.value / max) * 100 : 0;
                    return (
                        <div key={t.key} className="powerFacetTypeRow">
                            <span className="powerFacetTypeLabel">{t.label}</span>
                            <div className="powerFacetTypeBarTrack">
                                <div
                                    className="powerFacetTypeBarFill"
                                    style={{ width: `${w}%` }}
                                />
                            </div>
                            <span className="powerFacetTypeValue">{t.value}</span>
                        </div>
                    );
                })}
            </div>
            <span className="powerFacetSubNote">
                type-hits — a card with multiple types counts in each
            </span>
        </div>
    );
}

function CurveFacet({ curve, speed }) {
    if (!curve && !speed) return null;
    const avgMv = curve?.averageMv ?? speed?.averageCmc;
    const headline = CURVE_SHAPE_LABEL[curve?.shape];
    const stats = [
        curve?.peakMv != null ? { value: curve.peakMv, label: 'peak MV' } : null,
        curve?.subTwoCount ? { value: curve.subTwoCount, label: 'cheap (<2)' } : null,
        curve?.sixPlusCount ? { value: curve.sixPlusCount, label: 'finishers (6+)' } : null,
    ];
    const badges = [
        speed?.lowCurveDense && { tone: 'alert', label: 'Turns over fast' },
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Curve & speed" density={speed?.efficiency} headline={headline} />
            <BigNumber
                value={typeof avgMv === 'number' ? avgMv.toFixed(2) : null}
                label="avg MV"
            />
            <StatRow stats={stats} />
            <Badges items={badges} />
        </div>
    );
}

function TimingFacet({ timing }) {
    if (!timing || (!timing.instantSpeedCount && !timing.sorcerySpeedCount)) return null;
    const pct = Math.round((timing.instantSpeedRatio || 0) * 100);
    const stats = [
        timing.instantSpeedCount ? { value: timing.instantSpeedCount, label: 'instant' } : null,
        timing.sorcerySpeedCount ? { value: timing.sorcerySpeedCount, label: 'sorcery' } : null,
    ];
    const badges = [
        timing.flashEnablerCount > 0 && {
            tone: 'alert',
            label: `${timing.flashEnablerCount} flash enabler${timing.flashEnablerCount > 1 ? 's' : ''}`,
        },
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Timing" density={timing.reactiveDensity} headline={`${pct}% instant-speed`} />
            <StatRow stats={stats} />
            <Badges items={badges} />
        </div>
    );
}

function EngineFacet({ engine }) {
    if (!engine) return null;
    const hasData =
        engine.etbTriggerCount ||
        engine.diesTriggerCount ||
        engine.flickerEnablerCount ||
        engine.repeatableValueDensity !== 'none' ||
        engine.oneShotValueDensity !== 'none';
    if (!hasData) return null;
    const stats = [
        engine.etbTriggerCount ? { value: engine.etbTriggerCount, label: 'ETB' } : null,
        engine.diesTriggerCount ? { value: engine.diesTriggerCount, label: 'dies' } : null,
    ];
    const badges = [
        engine.flickerEnablerCount > 0 && {
            tone: 'alert',
            label: `${engine.flickerEnablerCount} flicker enabler${engine.flickerEnablerCount > 1 ? 's' : ''}`,
        },
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Engine" />
            <StatRow stats={stats} />
            <div className="powerFacetDensityRows">
                <DensityRow label="Repeatable" density={engine.repeatableValueDensity} />
                <DensityRow label="One-shot" density={engine.oneShotValueDensity} />
            </div>
            <Badges items={badges} />
        </div>
    );
}

function GraveyardFacet({ graveyard }) {
    if (!graveyard) return null;
    const hasData =
        graveyard.engagement !== 'none' ||
        graveyard.reanimationCount ||
        graveyard.graveyardPayoffCount ||
        graveyard.selfMillCount;
    if (!hasData) return null;
    const stats = [
        graveyard.reanimationCount ? { value: graveyard.reanimationCount, label: 'reanim' } : null,
        graveyard.graveyardPayoffCount ? { value: graveyard.graveyardPayoffCount, label: 'payoffs' } : null,
        graveyard.selfMillCount ? { value: graveyard.selfMillCount, label: 'self-mill' } : null,
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Graveyard" density={graveyard.engagement} />
            <StatRow stats={stats} />
            <div className="powerFacetDensityRows">
                <DensityRow label="Recursion" density={graveyard.recursionDensity} />
            </div>
        </div>
    );
}

function TypalFacet({ typal }) {
    if (!typal || !typal.dominantType) return null;
    if (!typal.isFocused && (typal.dominantTypeRatio || 0) < 0.2) return null;
    const pct = Math.round((typal.dominantTypeRatio || 0) * 100);
    const typeWord = capitalize(typal.dominantType);
    const headline = typal.isFocused ? `${typeWord} tribal` : `Leans ${typeWord}`;
    const stats = [
        { value: typal.dominantTypeCount, label: `${typeWord.toLowerCase()}s` },
        { value: `${pct}%`, label: 'of creatures' },
        typal.typalPayoffCount ? { value: typal.typalPayoffCount, label: 'payoffs' } : null,
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Typal" headline={headline} />
            <StatRow stats={stats} />
        </div>
    );
}

function CommanderRelianceFacet({ commanderReliance }) {
    if (!commanderReliance) return null;
    const hasData =
        commanderReliance.reliance !== 'none' ||
        commanderReliance.typeMatchCount ||
        commanderReliance.equipmentAuraCount ||
        commanderReliance.themePayoffCount;
    if (!hasData) return null;
    const headline =
        commanderReliance.reliance === 'dense'
            ? 'Built around commander'
            : commanderReliance.reliance === 'none'
            ? 'Generic shell'
            : null;
    const stats = [
        commanderReliance.typeMatchCount
            ? { value: commanderReliance.typeMatchCount, label: 'type match' }
            : null,
        commanderReliance.equipmentAuraCount
            ? { value: commanderReliance.equipmentAuraCount, label: 'equip/aura' }
            : null,
        commanderReliance.themePayoffCount
            ? { value: commanderReliance.themePayoffCount, label: 'theme' }
            : null,
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Commander reliance" density={commanderReliance.reliance} headline={headline} />
            <StatRow stats={stats} />
        </div>
    );
}

export function PowerLevelFingerprint({ profile }) {
    if (!profile) return null;
    return (
        <div className="powerFacetGrid">
            <ManabaseFacet manabase={profile.manabase} />
            <CompositionFacet composition={profile.composition} />
            <CurveFacet curve={profile.curve} speed={profile.speed} />
            <RampFacet ramp={profile.ramp} />
            <TutorsFacet tutors={profile.tutors} />
            <CardAdvantageFacet cardAdvantage={profile.cardAdvantage} />
            <InteractionFacet interaction={profile.interaction} />
            <ResourceDenialFacet resourceDenial={profile.resourceDenial} />
            <TimingFacet timing={profile.timing} />
            <WinconsFacet wincons={profile.wincons} />
            <CombosFacet combos={profile.combos} />
            <EngineFacet engine={profile.engine} />
            <GraveyardFacet graveyard={profile.graveyard} />
            <SynergyFacet synergy={profile.synergy} />
            <TypalFacet typal={profile.typal} />
            <CommanderRelianceFacet commanderReliance={profile.commanderReliance} />
        </div>
    );
}
